import { fetchWithRetry } from '../utils/retryLogic';
import { logError, ApiError } from '../utils/errorHandler';
import { API_CONFIG } from '../config';

const ApiEndPoint = API_CONFIG.ENDPOINTS.API;
const LogsApiEndPoint = API_CONFIG.ENDPOINTS.LOGS;
const BearerToken = API_CONFIG.BEARER_TOKEN;

/**
 * Make a GET request with retry logic
 * @param {string} urlPath - Path to append to API endpoint
 * @returns {Promise<any>} - Parsed JSON response
 */
export const getRequest = async (urlPath) => {
    const url = ApiEndPoint + urlPath;

    try {
        const response = await fetchWithRetry(url, {
            method: "GET",
            headers: {
                "Authorization": `Bearer ${BearerToken}`,
                "Content-Type": "application/json"
            }
        });

        return response.json();
    } catch (error) {
        logError(error, { urlPath, method: 'GET' });
        throw error;
    }
};

/**
 * Make a POST request with retry logic
 * @param {string} urlPath - Path to append to API endpoint
 * @param {Object} data - Request body
 * @returns {Promise<any>} - Parsed JSON response
 */
export const postRequest = async (urlPath, data) => {
    const url = ApiEndPoint + urlPath;

    try {
        const response = await fetchWithRetry(url, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${BearerToken}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(data)
        });

        return response.json();
    } catch (error) {
        logError(error, { urlPath, method: 'POST', data });
        throw error;
    }
};

/**
 * Log events to the logging API
 * IMPORTANT: This function should NOT throw errors - logging failures shouldn't break the app
 * @param {Object} impressionData - Event data to log
 * @param {Object} sessionData - Session context
 * @returns {Promise<any|null>} - Response or null on failure
 */
export const logs = async (impressionData, sessionData) => {
    try {
        const url = `${LogsApiEndPoint}/logging/${impressionData.event}/${sessionData.client_mac_norm}/${sessionData.ap_mac_norm}`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(impressionData)
        });

        if (!response.ok) {
            throw new ApiError('Logging failed', response.status, { impressionData });
        }

        return response.json();
    } catch (error) {
        // Log error but don't throw - logging failures shouldn't break the app
        logError(error, { impressionData, sessionData, context: 'logs_api' });
        return null;
    }
};